import { shuffle } from '~/utils/helpers'
import { randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'

/**
 * generate equation and result
 * @param min minimum number to generate numbers
 * @param max maximum number to generate numbers
 * @param taskHistoryEquation history of equation
 * @param additionCount total addition in equation
 * @param subtractionCount total subtraction in equation
 * @param multiplicationCount total multiplication in equation
 * @returns object containing equation and result
 */
export function generateEquationAndResult(
  min: number,
  max: number,
  taskHistoryEquation: string[],
  additionCount: number,
  subtractionCount: number,
  multiplicationCount: number,
): { equation: string; result: number } {
  let operators = generateOperators(additionCount, subtractionCount, multiplicationCount)
  let numbers = Array(operators.length + 1).fill(0)
  numbers = numbers.map(() => randomIntFromInterval(min, max))
  let equation = generateEquationFromNumberAndOperators(numbers, operators)
  let evalString = equation.replaceAll('×', '*')
  let result = eval(evalString)
  // make the result positive integer
  if (result < 0) {
    if (additionCount + subtractionCount >= 2) {
      operators = operators.map((operator) => {
        if (operator === '+') {
          return '-'
        } else if (operator === '-') {
          return '+'
        } else {
          return operator
        }
      })
    } else {
      if (operators[0] === '-') [operators[0], operators[1]] = [operators[1], operators[0]]
      makePositiveAnswerForOneNegativeOperator(operators, numbers)
    }
    equation = generateEquationFromNumberAndOperators(numbers, operators)
    evalString = equation.replaceAll('×', '*')
    result = eval(evalString)
  }
  // recalculate if equation is in history
  if (taskHistoryEquation.includes(equation)) {
    return generateEquationAndResult(
      min,
      max,
      taskHistoryEquation,
      additionCount,
      subtractionCount,
      multiplicationCount,
    )
  }
  return { equation, result }
}

/**
 * Make the result positive integer
 * @param operators operators to check
 * @param numbers numbers to check
 */
function makePositiveAnswerForOneNegativeOperator(operators: string[], numbers: number[]): void {
  const equation = generateEquationFromNumberAndOperators(numbers, operators)
  const evalString = equation.replaceAll('×', '*')
  const result = eval(evalString)
  if (result < 0) {
    numbers[0]++
    makePositiveAnswerForOneNegativeOperator(operators, numbers)
  }
}

/**
 * Generates random operators for equation
 * @param additionCount total addition in equation
 * @param subtractionCount total subtraction in equation
 * @param multiplicationCount total multiplication in equation
 * @returns array of operators
 */
function generateOperators(additionCount = 1, subtractionCount = 0, multiplicationCount = 1): string[] {
  const totalOperators = additionCount + subtractionCount + multiplicationCount
  let operators: string[] = Array(totalOperators).fill('')
  operators = operators.map(() => {
    if (additionCount > 0) {
      additionCount--
      return '+'
    } else if (subtractionCount > 0) {
      subtractionCount--
      return '-'
    } else if (multiplicationCount > 0) {
      multiplicationCount--
      return '×'
    } else {
      return '+'
    }
  })
  return shuffle(operators)
}

/**
 * Generates equation from numbers and result
 * @param numbers numbers to check
 * @param operator operator to check
 * @returns return the equation
 */
function generateEquationFromNumberAndOperators(numbers: number[], operator: string[]): string {
  let equation = ''
  numbers.forEach((item, index) => {
    if (index !== numbers.length - 1) {
      equation += `${item}  ${operator[index]} `
    }
  })
  equation += numbers[numbers.length - 1]
  return equation
}
